// Admin Integration Settings - LLM and SMTP Configuration
//
// Payment Gateways used to be a third tab here — it's now its own page at
// /admin/payment-gateways (AdminPaymentGateways.jsx), part of the Pricing &
// Payments sidebar reorg. See PROGRESS.md for the change.
import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { Mail, Sparkles, CheckCircle, XCircle, Loader2, Eye, EyeOff, TestTube } from 'lucide-react';

import { getApiBaseUrl } from '@/lib/apiBase';
const API_URL = getApiBaseUrl();

export const AdminIntegrations = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState({ smtp: false, llm: false });
  const [showPasswords, setShowPasswords] = useState({ smtp: false, gemini: false, openai: false });

  const [smtpSettings, setSmtpSettings] = useState({
    smtp_host: '',
    smtp_port: 587,
    smtp_user: '',
    smtp_password: '',
    smtp_from_email: '',
    is_configured: false
  });

  const [llmSettings, setLlmSettings] = useState({
    gemini_api_key: '',
    openai_api_key: '',
    use_emergent_key: true,
    is_configured: false
  });

  const headers = { Authorization: `Bearer ${token}` };

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/integrations`, { headers });
      if (res.data.smtp) setSmtpSettings(res.data.smtp);
      if (res.data.llm) setLlmSettings(res.data.llm);
    } catch (err) {
      console.error('Failed to fetch settings:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const saveSMTP = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/admin/integrations/smtp`, smtpSettings, { headers });
      toast.success('SMTP settings saved successfully');
      fetchSettings();
    } catch (err) {
      toast.error('Failed to save SMTP settings');
    } finally {
      setSaving(false);
    }
  };

  const saveLLM = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/admin/integrations/llm`, llmSettings, { headers });
      toast.success('LLM settings saved successfully');
      fetchSettings();
    } catch (err) {
      toast.error('Failed to save LLM settings');
    } finally {
      setSaving(false);
    }
  };

  const testSMTP = async () => {
    setTesting({ ...testing, smtp: true });
    try {
      const res = await axios.post(`${API_URL}/admin/integrations/test-smtp`, {}, { headers });
      if (res.data.success) {
        toast.success('SMTP connection successful!');
      } else {
        toast.error(`SMTP test failed: ${res.data.error}`);
      }
    } catch (err) {
      toast.error('SMTP test failed');
    } finally {
      setTesting({ ...testing, smtp: false });
    }
  };

  const testLLM = async () => {
    setTesting({ ...testing, llm: true });
    try {
      const res = await axios.post(`${API_URL}/admin/integrations/test-llm`, {}, { headers });
      if (res.data.success) {
        toast.success(res.data.message);
      } else {
        toast.error(`LLM test failed: ${res.data.error}`);
      }
    } catch (err) {
      toast.error('LLM test failed');
    } finally {
      setTesting({ ...testing, llm: false });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div data-testid="admin-integrations">
      <div className="mb-8">
        <h1 className="text-3xl font-serif font-medium">Integrations</h1>
        <p className="text-muted-foreground">Configure external services and API keys</p>
      </div>

      <Tabs defaultValue="email" className="space-y-6">
        <TabsList>
          <TabsTrigger value="email" className="flex items-center gap-2">
            <Mail className="w-4 h-4" />
            Email (SMTP)
          </TabsTrigger>
          <TabsTrigger value="ai" className="flex items-center gap-2">
            <Sparkles className="w-4 h-4" />
            AI / LLM
          </TabsTrigger>
        </TabsList>

        {/* SMTP Settings */}
        <TabsContent value="email">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>SMTP Email Configuration</CardTitle>
                  <CardDescription>Configure email sending for password resets, notifications, etc.</CardDescription>
                </div>
                <Badge variant={smtpSettings.is_configured ? 'default' : 'secondary'}>
                  {smtpSettings.is_configured ? (
                    <><CheckCircle className="w-3 h-3 mr-1" /> Configured</>
                  ) : (
                    <><XCircle className="w-3 h-3 mr-1" /> Not Configured</>
                  )}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>SMTP Host *</Label>
                  <Input
                    value={smtpSettings.smtp_host}
                    onChange={(e) => setSmtpSettings({ ...smtpSettings, smtp_host: e.target.value })}
                    placeholder="smtp.gmail.com"
                  />
                </div>
                <div>
                  <Label>SMTP Port</Label>
                  <Input
                    type="number"
                    value={smtpSettings.smtp_port}
                    onChange={(e) => setSmtpSettings({ ...smtpSettings, smtp_port: parseInt(e.target.value) })}
                    placeholder="587"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>SMTP Username *</Label>
                  <Input
                    value={smtpSettings.smtp_user}
                    onChange={(e) => setSmtpSettings({ ...smtpSettings, smtp_user: e.target.value })}
                    placeholder="your-email@gmail.com"
                  />
                </div>
                <div>
                  <Label>SMTP Password *</Label>
                  <div className="relative">
                    <Input
                      type={showPasswords.smtp ? 'text' : 'password'}
                      value={smtpSettings.smtp_password}
                      onChange={(e) => setSmtpSettings({ ...smtpSettings, smtp_password: e.target.value })}
                      placeholder="App password"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full"
                      onClick={() => setShowPasswords({ ...showPasswords, smtp: !showPasswords.smtp })}
                    >
                      {showPasswords.smtp ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">For Gmail, use an App Password</p>
                </div>
              </div>

              <div>
                <Label>From Email (optional)</Label>
                <Input
                  value={smtpSettings.smtp_from_email}
                  onChange={(e) => setSmtpSettings({ ...smtpSettings, smtp_from_email: e.target.value })}
                  placeholder="noreply@yourdomain.com (defaults to SMTP username)"
                />
              </div>

              <div className="flex items-center gap-4 pt-4">
                <Button onClick={saveSMTP} disabled={saving}>
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Save SMTP Settings
                </Button>
                <Button variant="outline" onClick={testSMTP} disabled={testing.smtp || !smtpSettings.smtp_host}>
                  {testing.smtp ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <TestTube className="w-4 h-4 mr-2" />}
                  Test Connection
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* LLM Settings */}
        <TabsContent value="ai">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>AI / LLM Configuration</CardTitle>
                  <CardDescription>Configure AI features for content generation</CardDescription>
                </div>
                <Badge variant={llmSettings.is_configured ? 'default' : 'secondary'}>
                  {llmSettings.is_configured ? (
                    <><CheckCircle className="w-3 h-3 mr-1" /> Configured</>
                  ) : (
                    <><XCircle className="w-3 h-3 mr-1" /> Not Configured</>
                  )}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Emergent Key Toggle */}
              <Card className="p-4 bg-muted/50">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium">Use Emergent Universal Key</h4>
                    <p className="text-sm text-muted-foreground">
                      Recommended: Uses Emergent's managed API key for Gemini. No setup required.
                    </p>
                  </div>
                  <Switch
                    checked={llmSettings.use_emergent_key}
                    onCheckedChange={(v) => setLlmSettings({ ...llmSettings, use_emergent_key: v })}
                  />
                </div>
              </Card>

              {/* Custom Keys (shown when Emergent key is disabled) */}
              {!llmSettings.use_emergent_key && (
                <div className="space-y-4">
                  <div>
                    <Label>Google Gemini API Key</Label>
                    <div className="relative">
                      <Input
                        type={showPasswords.gemini ? 'text' : 'password'}
                        value={llmSettings.gemini_api_key}
                        onChange={(e) => setLlmSettings({ ...llmSettings, gemini_api_key: e.target.value })}
                        placeholder="AIza..."
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-0 top-0 h-full"
                        onClick={() => setShowPasswords({ ...showPasswords, gemini: !showPasswords.gemini })}
                      >
                        {showPasswords.gemini ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Get your API key from <a href="https://makersuite.google.com/app/apikey" target="_blank" rel="noopener noreferrer" className="underline">Google AI Studio</a>
                    </p>
                  </div>

                  <div>
                    <Label>OpenAI API Key (optional)</Label>
                    <div className="relative">
                      <Input
                        type={showPasswords.openai ? 'text' : 'password'}
                        value={llmSettings.openai_api_key}
                        onChange={(e) => setLlmSettings({ ...llmSettings, openai_api_key: e.target.value })}
                        placeholder="sk-..."
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-0 top-0 h-full"
                        onClick={() => setShowPasswords({ ...showPasswords, openai: !showPasswords.openai })}
                      >
                        {showPasswords.openai ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Get your API key from <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="underline">OpenAI Platform</a>
                    </p>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-4 pt-4">
                <Button onClick={saveLLM} disabled={saving}>
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Save AI Settings
                </Button>
                <Button variant="outline" onClick={testLLM} disabled={testing.llm}>
                  {testing.llm ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <TestTube className="w-4 h-4 mr-2" />}
                  Test AI Connection
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminIntegrations;
